/**
 * Type-to-filter picker for a device facet (team / folder).
 *
 * Same anchored-listbox interaction as the quote form's `CountryCombobox`
 * (`features/optihub/quote/components/CountryCombobox.tsx`) and for the same
 * reason: `@staff-app/ui` carries no Command/Combobox primitive, scio3's
 * MultiSelectCombobox is off-limits (ESLint blocks `@scio3/*`), and Radix
 * `Popover` moves focus into the panel on open, which fights typing.
 *
 * Differs in that this one is selection-only — the options are a closed set, so
 * free text never commits; it just narrows the list. Counts sit on the right so
 * an empty team (`MATTINGLEY — 0`) is visible before you pick it.
 */
import { useId, useMemo, useRef, useState } from 'react';
import { Checkbox, Button, Input, cn } from '@staff-app/ui';
import { ChevronsUpDown, X } from 'lucide-react';
import {
  toggleFacetValue,
  type FacetOption,
  type FacetSelection,
  type FacetValue,
} from '../utils/device-facets';

interface Props {
  /** Singular noun for a11y + summaries, e.g. "Team". */
  label: string;
  /** Shown when nothing is picked, e.g. "All teams". */
  allLabel: string;
  /** Plural noun for the multi-pick summary, e.g. "teams" → "3 teams". */
  pluralLabel: string;
  options: FacetOption[];
  value: FacetSelection;
  onChange: (value: FacetValue[]) => void;
  testId: string;
}

/** Prefix matches rank above substring, so typing "kona" offers /KONA ICE first. */
function rank(label: string, q: string): number | null {
  const l = label.toLowerCase();
  if (l === q) return 0;
  if (l.startsWith(q)) return 1;
  if (l.includes(q)) return 2;
  return null;
}

export function FacetCombobox({
  label,
  allLabel,
  pluralLabel,
  options,
  value,
  onChange,
  testId,
}: Props) {
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  /** null = not typing; show the committed selection instead of a draft. */
  const [draft, setDraft] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  // One pick reads better as its own name; several collapse to a count, since
  // concatenated folder paths overflow the field immediately.
  const summary =
    value.length === 0
      ? ''
      : value.length === 1
        ? (options.find((o) => o.value === value[0])?.label ?? String(value[0]))
        : `${value.length} ${pluralLabel}`;
  const shown = draft ?? summary;

  const matches = useMemo(() => {
    const q = (draft ?? '').trim().toLowerCase();
    if (!q) return options;
    return options
      .map((o) => ({ o, r: rank(o.label, q) }))
      .filter((x): x is { o: FacetOption; r: number } => x.r !== null)
      .sort((a, b) => a.r - b.r || (b.o.count ?? 0) - (a.o.count ?? 0) || a.o.label.localeCompare(b.o.label))
      .map((x) => x.o);
  }, [draft, options]);

  /** Toggling keeps the list open — the whole point is picking several. */
  const toggle = (option: FacetValue) => {
    onChange(toggleFacetValue(value, option));
    setDraft(null);
  };

  const clearAll = () => {
    onChange([]);
    setDraft(null);
    setOpen(false);
    inputRef.current?.blur();
  };

  return (
    <div className="relative min-w-[168px] flex-1">
      <div className="relative">
        <Input
          ref={inputRef}
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-label={label}
          autoComplete="off"
          value={shown}
          placeholder={allLabel}
          data-testid={testId}
          onChange={(e) => {
            setDraft(e.target.value);
            setOpen(true);
            setActive(0);
          }}
          onFocus={() => setOpen(true)}
          // Also on click: Escape closes the list WITHOUT blurring, so a focused
          // input would otherwise never reopen on the next click.
          onClick={() => setOpen(true)}
          // Typing then leaving without picking keeps the previous selection —
          // a half-typed folder name is not a filter.
          onBlur={() => {
            setDraft(null);
            setOpen(false);
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
              e.preventDefault();
              setOpen(true);
              setActive((i) => {
                const next = e.key === 'ArrowDown' ? i + 1 : i - 1;
                return Math.max(0, Math.min(matches.length - 1, next));
              });
              return;
            }
            if (e.key === 'Enter') {
              if (open && matches[active]) {
                e.preventDefault();
                toggle(matches[active].value);
              }
              return;
            }
            if (e.key === 'Escape') {
              setDraft(null);
              setOpen(false);
            }
          }}
          className={cn('pr-8', value.length > 0 && 'pr-14')}
        />
        <div className="pointer-events-none absolute right-1.5 top-1/2 flex -translate-y-1/2 items-center gap-0.5">
          {value.length > 0 ? (
            <Button
              variant="ghost"
              size="icon-xs"
              aria-label={`Clear ${label} filter`}
              className="pointer-events-auto cursor-pointer"
              data-testid={`${testId}-clear`}
              // mousedown, not click: click lands after the input's blur has
              // already closed the list and re-rendered the row.
              onMouseDown={(e) => {
                e.preventDefault();
                clearAll();
              }}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          ) : null}
          <ChevronsUpDown className="h-4 w-4 text-muted-foreground" />
        </div>
      </div>

      {open ? (
        <ul
          id={listId}
          role="listbox"
          aria-label={label}
          className="absolute z-50 mt-1 max-h-72 w-full min-w-[260px] overflow-y-auto rounded-md border bg-popover p-1 shadow-md"
          data-testid={`${testId}-list`}
        >
          <li
            role="option"
            aria-selected={value.length === 0}
            onMouseDown={(e) => {
              e.preventDefault();
              clearAll();
            }}
            className="flex cursor-pointer items-center justify-between rounded-sm px-2 py-1.5 text-sm text-muted-foreground hover:bg-accent/50 transition-colors"
            data-testid={`${testId}-all`}
          >
            <span>{allLabel}</span>
            {value.length > 0 ? <span className="text-xs">{`Clear ${value.length}`}</span> : null}
          </li>

          {matches.length === 0 ? (
            <li className="px-2 py-1.5 text-sm text-muted-foreground" data-testid={`${testId}-no-match`}>
              {`No ${label.toLowerCase()} matches`}
            </li>
          ) : (
            matches.map((o, i) => {
              const checked = value.includes(o.value);
              return (
                <li
                  key={o.value ?? '__none__'}
                  role="option"
                  aria-selected={checked}
                  // mousedown keeps focus in the input, so the list stays open
                  // across several picks.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    toggle(o.value);
                  }}
                  onMouseEnter={() => setActive(i)}
                  className={cn(
                    'flex cursor-pointer items-center justify-between gap-3 rounded-sm px-2 py-1.5 text-sm',
                    i === active ? 'bg-accent text-accent-foreground' : 'text-foreground',
                  )}
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <Checkbox
                      checked={checked}
                      // The row owns the click; the box is a visual state marker.
                      tabIndex={-1}
                      aria-hidden
                      className="pointer-events-none shrink-0"
                    />
                    <span className="min-w-0 truncate" title={o.label}>
                      {o.label}
                    </span>
                  </span>
                  {o.count !== undefined ? (
                    <span
                      className={cn(
                        'shrink-0 tabular-nums text-xs',
                        o.count === 0 ? 'text-muted-foreground/60' : 'text-muted-foreground',
                      )}
                    >
                      {o.count}
                    </span>
                  ) : null}
                </li>
              );
            })
          )}
        </ul>
      ) : null}
    </div>
  );
}
